import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, ArrowRight, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Errore di accesso");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#050B14] flex flex-col text-slate-100"
    >
      <header className="border-b border-[#1E293B] py-5 px-6 md:px-10">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm"
        >
          <ArrowLeft size={16} /> Torna al sito
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#FF5A1F] flex items-center justify-center">
              <Lock size={18} className="text-white" />
            </div>
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
                Area Riservata
              </div>
              <div className="text-[10px] tracking-wider uppercase text-slate-500">
                Dea srl
              </div>
            </div>
          </div>

          <h1 className="headline text-white text-4xl md:text-5xl mt-6">
            Accedi alla{" "}
            <span className="font-display italic font-medium text-slate-300">
              dashboard.
            </span>
          </h1>
          <p className="text-slate-400 text-sm mt-4">
            Visualizza preventivi e messaggi ricevuti dal sito e da WhatsApp.
          </p>

          <form
            onSubmit={onSubmit}
            data-testid="login-form"
            className="mt-10 bg-[#0A1120] border border-[#1E293B] p-8"
          >
            <label className="block">
              <span className="text-xs tracking-[0.25em] uppercase text-slate-500 font-semibold">
                Email
              </span>
              <input
                data-testid="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="dea-input mt-2"
                placeholder="admin@deagarlasco.it"
              />
            </label>

            <label className="block mt-5">
              <span className="text-xs tracking-[0.25em] uppercase text-slate-500 font-semibold">
                Password
              </span>
              <input
                data-testid="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="dea-input mt-2"
                placeholder="••••••••"
              />
            </label>

            {error && (
              <div
                data-testid="login-error"
                className="mt-5 flex items-center gap-2 bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#FCA5A5] text-sm p-3"
              >
                <AlertCircle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#FF5A1F] hover:bg-[#EA450C] disabled:opacity-60 text-white px-6 py-4 text-sm font-semibold transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Accesso…
                </>
              ) : (
                <>
                  Accedi <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-xs text-slate-500 leading-relaxed">
            Hai dimenticato la password? Contatta l'amministratore tecnico.
          </p>
        </div>
      </main>
    </div>
  );
}
